/**
 * AI-generated code, moderated by lzygmanski
 */
import dayjs from 'dayjs';

import i18n from '@/i18n';
import type {
  HealthLogMeasurementInput,
  HealthLogMetricDefinition,
  ResidentBaseFragmentFragment,
  Room,
} from '@/services/graphqlHooks';

import type {
  MeasurementValuesMap,
  ResidentMeasurementRow,
  ResidentWithRoom,
} from '../types/healthLogMeasurementsDialog';

/**
 * Merges residents with room data using a room map for O(n) performance.
 * No nested loops, no mutation.
 */
export function mergeResidentsWithRooms(
  residents: ResidentBaseFragmentFragment[],
  rooms: Omit<Room, 'createdAt' | 'updatedAt'>[]
): ResidentWithRoom[] {
  // Create roomId → room map for O(1) lookup
  const roomMap = new Map<string, Omit<Room, 'createdAt' | 'updatedAt'>>();
  for (const room of rooms) {
    roomMap.set(room.id, room);
  }

  // Map residents with room data
  return residents.map(resident => ({
    ...resident,
    roomNumber: roomMap.get(resident.roomId || '')?.roomNumber || '-',
  }));
}

/**
 * Builds table data from residents and measurement values.
 * Pure transformation, no mutation.
 */
export function buildTableData(
  residents: ResidentWithRoom[],
  measurementValues: MeasurementValuesMap
): ResidentMeasurementRow[] {
  return residents.map(resident => ({
    resident,
    values: measurementValues.get(resident.id) ?? {},
  }));
}

/**
 * Type definitions for validation results
 */
type ValidationResult =
  | {success: true; numericValues: Record<string, number>}
  | {success: false; error: string};

type ResidentEntry = {
  residentId: string;
  fieldValues: Record<string, string>;
};

/**
 * Checks if a resident has any measurement values entered.
 */
function hasAnyValues(fieldValues: Record<string, string>): boolean {
  return Object.values(fieldValues).some(value => value.trim() !== '');
}

/**
 * Validates a single field value and converts to number.
 * Pure function - no side effects.
 */
function validateFieldValue(
  value: string | undefined
): {valid: true; numericValue: number} | {valid: false; error: string} {
  if (!value || value.trim() === '') {
    return {
      valid: false,
      error: i18n.t('All fields must be filled for each resident with values'),
    };
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return {
      valid: false,
      error: i18n.t('All values must be valid numbers'),
    };
  }

  return {valid: true, numericValue};
}

/**
 * Validates all field values for a resident.
 * Returns numeric values or validation error.
 * Pure function - no mutation.
 */
function validateResidentFields(
  fieldValues: Record<string, string>,
  metricFields: HealthLogMetricDefinition['fields']
): ValidationResult {
  const numericValues: Record<string, number> = {};

  for (const field of metricFields) {
    const validation = validateFieldValue(fieldValues[field.name]);

    if (!validation.valid) {
      return {success: false, error: validation.error};
    }

    numericValues[field.name] = validation.numericValue;
  }

  return {success: true, numericValues};
}

/**
 * Builds a single measurement input from validated data.
 * Pure function - no side effects.
 */
function buildSingleMeasurementInput(
  residentId: string,
  numericValues: Record<string, number>,
  metricId: string,
  recordedAt: string
): HealthLogMeasurementInput {
  return {
    residentId,
    metricId,
    recordedAt: dayjs(recordedAt).toISOString(),
    values: JSON.stringify(numericValues),
  };
}

/**
 * Processes a single resident's measurement data.
 * Returns input or null if resident has no values.
 * Pure function.
 */
function processResidentMeasurement(
  entry: ResidentEntry,
  selectedMetric: HealthLogMetricDefinition,
  recordedAt: string
): {input: HealthLogMeasurementInput} | {error: string} | null {
  // Skip residents with no values entered
  if (!hasAnyValues(entry.fieldValues)) {
    return null;
  }

  // Validate all field values
  const validation = validateResidentFields(
    entry.fieldValues,
    selectedMetric.fields
  );

  if (!validation.success) {
    return {error: validation.error};
  }

  // Build measurement input
  const input = buildSingleMeasurementInput(
    entry.residentId,
    validation.numericValues,
    selectedMetric.id,
    recordedAt
  );

  return {input};
}

/**
 * Validates and builds measurement inputs for submission.
 * Pure function - no mutation, no nested loops.
 * Uses functional composition for readability.
 */
export function buildMeasurementInputs(
  measurementValues: MeasurementValuesMap,
  selectedMetric: HealthLogMetricDefinition,
  recordedAt: string
): {inputs: HealthLogMeasurementInput[]; error?: string} | null {
  // Convert Map to array of entries for functional processing
  const residentEntries: ResidentEntry[] = Array.from(
    measurementValues.entries()
  ).map(([residentId, fieldValues]) => ({residentId, fieldValues}));

  // Process each resident's measurements
  const results = residentEntries.map(entry =>
    processResidentMeasurement(entry, selectedMetric, recordedAt)
  );

  // Check for validation errors (early return on first error)
  const errorResult = results.find(
    result => result !== null && 'error' in result
  );
  if (errorResult && 'error' in errorResult) {
    return {inputs: [], error: errorResult.error};
  }

  // Extract successful inputs (filter out nulls)
  const inputs = results
    .filter(
      (result): result is {input: HealthLogMeasurementInput} =>
        result !== null && 'input' in result
    )
    .map(result => result.input);

  // Check if at least one measurement was entered
  if (inputs.length === 0) {
    return {
      inputs: [],
      error: i18n.t(
        'Please enter measurement values for at least one resident'
      ),
    };
  }

  return {inputs};
}
